import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Shield, Zap, Eye, Monitor, Clock, Settings, Activity } from 'lucide-react';
import api from '../lib/api';
import SEO from '../components/SEO';

function HeroSection() {
  return (
    <section data-testid="hero-section" className="relative min-h-[85vh] flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1760246964044-1384f71665b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc2Mjg2OTc2fDA&ixlib=rb-4.1.0&q=85"
          alt="Modern iroda"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0B1B3D]/75" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
        <div className="max-w-3xl">
          <div className="opacity-0 animate-fade-in-up">
            <span className="inline-block text-[#C5A059] text-xs uppercase tracking-[0.2em] font-semibold mb-6">
              Professzionális könyvelés
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Outfit'] font-light text-white tracking-tight leading-tight opacity-0 animate-fade-in-up animation-delay-100">
            Könyvelés, amire{' '}
            <span className="font-semibold">építhet</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-[#94A3B8] leading-relaxed max-w-2xl opacity-0 animate-fade-in-up animation-delay-200">
            Digitális működés, szakértői háttér, teljes átláthatóság. Fókuszáljon vállalkozására — a könyveléssel mi foglalkozunk.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up animation-delay-300">
            <Link to="/kapcsolat">
              <Button data-testid="hero-cta-primary" size="lg"
                className="bg-[#C5A059] hover:bg-[#B08D4A] text-[#0B1B3D] font-semibold text-base px-8 py-6 rounded-lg w-full sm:w-auto">
                Ajánlatkérés <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/szolgaltatasok">
              <Button data-testid="hero-cta-secondary" variant="outline" size="lg"
                className="border-white/30 text-white hover:bg-white/10 font-medium text-base px-8 py-6 rounded-lg w-full sm:w-auto">
                Szolgáltatások
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  const items = [
    { icon: Zap, title: 'Gyorsaság', desc: 'Határidő előtt kész, mindig naprakész könyvelés és bevallások.' },
    { icon: Shield, title: 'Szakértelem', desc: 'Tapasztalt könyvelő és adószakértő csapat, akikre számíthat.' },
    { icon: Eye, title: 'Átláthatóság', desc: 'Mindig tudja, hol tart a pénzügyi helyzete. Valós időben.' },
    { icon: Monitor, title: 'Digitális működés', desc: 'Novitax rendszer, online hozzáférés, papírmentes ügyintézés.' },
  ];
  return (
    <section data-testid="trust-section" className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#C5A059] text-xs uppercase tracking-[0.2em] font-semibold">Miért minket</span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-['Outfit'] font-semibold text-[#0B1B3D] tracking-tight">
            Miért válasszanak minket?
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {items.map(({ icon: Icon, title, desc }, i) => (
            <div
              key={title}
              data-testid={`trust-item-${i}`}
              className="group p-8 bg-[#FAFAFA] border border-[#E2E8F0] rounded-lg hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 bg-[#0B1B3D] rounded-lg flex items-center justify-center mb-5 group-hover:bg-[#C5A059] transition-colors duration-300">
                <Icon size={22} className="text-white" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D] mb-2">{title}</h3>
              <p className="text-sm text-[#64748B] leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesPreview() {
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get('/service-categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  return (
    <section data-testid="services-preview" className="py-20 sm:py-28 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#C5A059] text-xs uppercase tracking-[0.2em] font-semibold">Szolgáltatásaink</span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-['Outfit'] font-semibold text-[#0B1B3D] tracking-tight">
            Miben segíthetünk?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#64748B] max-w-2xl mx-auto">
            Átfogó pénzügyi szolgáltatások, amelyekre vállalkozása építhet.
          </p>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to="/szolgaltatasok"
              data-testid={`preview-${cat.slug}`}
              className="group block bg-white border border-[#E2E8F0] rounded-lg p-6 sm:p-8 hover:-translate-y-0.5 hover:shadow-lg hover:border-[#C5A059]/30 transition-all duration-300"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl sm:text-2xl font-['Outfit'] font-semibold text-[#0B1B3D] group-hover:text-[#0B1B3D] transition-colors">
                    {cat.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#64748B]">{cat.description}</p>
                </div>
                <ArrowRight size={20} className="text-[#C5A059] opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 flex-shrink-0 ml-4" />
              </div>
            </Link>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/szolgaltatasok">
            <Button size="lg" className="bg-[#0B1B3D] hover:bg-[#162B56] text-white font-semibold px-10">
              Összes szolgáltatás <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function HowWeWork() {
  const steps = [
    { icon: Clock, num: '01', title: 'Kapcsolatfelvétel', desc: 'Vegye fel velünk a kapcsolatot online vagy telefonon. Felmérjük az igényeit és testre szabott ajánlatot adunk.' },
    { icon: Settings, num: '02', title: 'Beállítás', desc: 'Beállítjuk a digitális rendszert, hozzáférést biztosítunk a Novitax platformhoz, és átvesszük a könyvelését.' },
    { icon: Activity, num: '03', title: 'Folyamatos működés', desc: 'Havi szinten kezeljük a könyvelését, bevallásait, és folyamatosan informáljuk a pénzügyi helyzetéről.' },
  ];
  return (
    <section data-testid="how-we-work-section" className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#C5A059] text-xs uppercase tracking-[0.2em] font-semibold">Folyamat</span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-['Outfit'] font-semibold text-[#0B1B3D] tracking-tight">
            Hogyan dolgozunk?
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map(({ icon: Icon, num, title, desc }, i) => (
            <div key={num} data-testid={`step-${i + 1}`} className="text-center group">
              <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6">
                <div className="absolute inset-0 bg-[#0B1B3D]/5 rounded-2xl group-hover:bg-[#C5A059]/10 transition-colors" />
                <Icon size={32} className="text-[#0B1B3D] group-hover:text-[#C5A059] transition-colors" strokeWidth={1.5} />
                <span className="absolute -top-2 -right-2 w-8 h-8 bg-[#0B1B3D] text-white text-xs font-semibold rounded-full flex items-center justify-center">
                  {num}
                </span>
              </div>
              <h3 className="text-xl font-['Outfit'] font-semibold text-[#0B1B3D] mb-3">{title}</h3>
              <p className="text-sm text-[#64748B] leading-relaxed max-w-xs mx-auto">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsPreview() {
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    api.get('/articles?limit=3').then(r => setArticles(r.data)).catch(() => {});
  }, []);

  if (articles.length === 0) return null;

  return (
    <section data-testid="news-preview" className="py-20 sm:py-28 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-[#C5A059] text-xs uppercase tracking-[0.2em] font-semibold">Hírek</span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-['Outfit'] font-semibold text-[#0B1B3D] tracking-tight">
              Aktualitások
            </h2>
          </div>
          <Link to="/hirek" className="hidden sm:flex items-center gap-1 text-[#0B1B3D] text-sm font-medium hover:text-[#C5A059] transition-colors">
            Összes hír <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article) => (
            <Link
              key={article.id}
              to={`/hirek/${article.slug}`}
              data-testid={`news-card-${article.slug}`}
              className="group bg-white border border-[#E2E8F0] rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
            >
              {article.featured_image && (
                <div className="aspect-video overflow-hidden">
                  <img
                    src={article.featured_image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${article.featured_image}` : article.featured_image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              )}
              <div className="p-6">
                <span className="text-xs text-[#64748B]">
                  {new Date(article.created_at).toLocaleDateString('hu-HU')}
                </span>
                <h3 className="mt-2 text-lg font-['Outfit'] font-semibold text-[#0B1B3D] group-hover:text-[#C5A059] transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="mt-2 text-sm text-[#64748B] line-clamp-2">{article.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-8 text-center sm:hidden">
          <Link to="/hirek">
            <Button variant="outline" className="border-[#0B1B3D] text-[#0B1B3D]">
              Összes hír <ArrowRight className="ml-2" size={16} />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function CTABlock() {
  return (
    <section
      data-testid="cta-block"
      className="relative py-20 sm:py-28 overflow-hidden"
      style={{ backgroundImage: `url(https://images.unsplash.com/photo-1709990740117-0eb9358fbd24?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxkYXJrJTIwYmx1ZSUyMGFic3RyYWN0JTIwdGV4dHVyZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc2Mjg2OTkyfDA&ixlib=rb-4.1.0&q=85)`, backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="absolute inset-0 bg-[#0B1B3D]/85" />
      <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-semibold text-white tracking-tight">
          Kérjen ajánlatot még ma
        </h2>
        <p className="mt-6 text-lg text-[#94A3B8] max-w-xl mx-auto">
          Testre szabott ajánlatot adunk 24 órán belül. Induljon el a stresszmentes könyvelés útján.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/kapcsolat">
            <Button data-testid="cta-bottom-primary" size="lg"
              className="bg-[#C5A059] hover:bg-[#B08D4A] text-[#0B1B3D] font-semibold px-10 py-6 text-base rounded-lg">
              Kapcsolatfelvétel <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
          <Link to="/szolgaltatasok">
            <Button data-testid="cta-bottom-secondary" variant="outline" size="lg"
              className="border-white/30 text-white hover:bg-white/10 px-10 py-6 text-base rounded-lg">
              Csomagok megtekintése
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO />
      <HeroSection />
      <TrustSection />
      <ServicesPreview />
      <HowWeWork />
      <NewsPreview />
      <CTABlock />
    </div>
  );
}
